import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import CloseIconButton from '../../../components/common/CloseIconButton';
import { ConfirmationModal } from '../../../components/confirmationModal';
import {
  PaymentModeToggle,
  PaymentDetailsCard,
  PaymentHistoryList,
  PaymentMethodSelector,
  CashPaymentForm,
  OnlinePaymentForm,
  CardPaymentForm,
  ChequePaymentForm,
  UPIPaymentForm,
  WalletPaymentForm,
  CreditInfoBanner,
  ConsumePackageBanner,
} from './components';
import {
  CollectPaymentModalProps,
  PaymentFormValues,
  PaymentHistoryItem,
  PaymentMethodType,
} from './collectPayment.types';
import { INITIAL_PAYMENT_VALUES } from './collectPayment.constants';
import {
  calculateRemainingAmount,
  formatCurrency,
  validatePaymentAmount,
  usePatientWalletBalance,
} from './collectPayment.helper';
import useCurrency from '../../../components/CurrencyProvider';
import { getTodayYMD } from '../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const CollectPaymentModal: React.FC<CollectPaymentModalProps> = ({
  open,
  onClose,
  onSave,
  paymentDetails,
  existingPaymentHistory = [],
  walletBalance: walletBalanceProp = 0,
  walletAdvances = [],
  paymentModes = [],
  upiProviders,
  showWallet = false,
  maxCreditAmount,
  hidePaymentModeToggle = false,
  consumeFromPackage = 0,
  isSubmitting = false,
  packageBalance,
  packageName,
  title,
  consumePackageBannerLabel,
  consumePackageTotalLabel,
  consumePackageDeductedLabel,
  consumePackagePendingLabel,
  requireFullPayment = false,
}) => {
  const { t: tBilling } = useTranslation('billing_config');
  // Get currency from provider as default
  const { currency: providerCurrency } = useCurrency();
  
  // Fetch patient wallet balance from API - only when modal is open and wallet is shown
  const { data: patientWalletData } = usePatientWalletBalance(
    paymentDetails.patientNumericId || undefined,
    { enabled: open && showWallet }
  );
  // Use API data if available, otherwise fallback to prop
  const walletBalance = patientWalletData?.balance_amount != null 
    ? Number(patientWalletData.balance_amount) 
    : walletBalanceProp;
  
  // Unique ID counter for payment items (avoids collisions from Date.now() + Math.random())
  const paymentIdCounterRef = useRef(0);
  const generatePaymentId = useCallback(() => {
    paymentIdCounterRef.current += 1;
    return `${Date.now()}-${paymentIdCounterRef.current}`;
  }, []);
  
  // Form state
  const [formValues, setFormValues] = useState<PaymentFormValues>(INITIAL_PAYMENT_VALUES);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryItem[]>(existingPaymentHistory);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(paymentDetails.currency || providerCurrency);
  const [savePaymentConfirmOpen, setSavePaymentConfirmOpen] = useState(false);

  // Available payment methods (exclude wallet unless showWallet is true)
  const availablePaymentMethods: PaymentMethodType[] = useMemo(() => {
    const methods: PaymentMethodType[] = ['cash', 'online', 'card', 'cheque', 'upi'];
    if (showWallet) {
      methods.push('wallet');
    }
    return methods;
  }, [showWallet]);
  
  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      let initialFormValues = { ...INITIAL_PAYMENT_VALUES };

      // Auto-populate the default payment method amount with the net amount
      const autoAmount = Math.max(0, (paymentDetails.netAmount || 0) - consumeFromPackage);
      if (autoAmount > 0) {
        initialFormValues = {
          ...initialFormValues,
          cash: {
            ...initialFormValues.cash,
            amount: autoAmount,
          },
        };
      }
      
      // If existingPaymentHistory has wallet payments, auto-select wallet method
      if (existingPaymentHistory && existingPaymentHistory.length > 0) {
        const walletPayment = existingPaymentHistory.find((p) => p.method === 'Wallet');
        if (walletPayment) {
          initialFormValues = {
            ...initialFormValues,
            paymentMethod: 'wallet',
            wallet: {
              amount: walletPayment.amount || 0,
              advances: walletAdvances,
              notes: walletPayment.notes || '',
            },
          };
        }
      }
      
      setFormValues(initialFormValues);
      setPaymentHistory(existingPaymentHistory);
      setErrors({});
      setSelectedCurrency(paymentDetails.currency);
    }
  }, [open, existingPaymentHistory, paymentDetails.currency, paymentDetails.netAmount, consumeFromPackage, walletAdvances]);

  // Calculate remaining amount (netAmount - consumeFromPackage - payments already made)
  const remainingAmount = useMemo(
    () => calculateRemainingAmount(paymentDetails.netAmount - consumeFromPackage, paymentHistory),
    [paymentDetails.netAmount, consumeFromPackage, paymentHistory]
  );

  // When package advance fully covers the net amount, hide payment methods entirely
  const isFullyCoveredByPackage = consumeFromPackage > 0 && consumeFromPackage >= paymentDetails.netAmount;

  // Calculate effective wallet balance (subtract wallet payments already in history)
  const effectiveWalletBalance = useMemo(() => {
    const walletPaymentsInHistory = paymentHistory
      .filter((p) => p.method === 'Wallet')
      .reduce((sum, p) => sum + p.amount, 0);
    return Math.max(0, walletBalance - walletPaymentsInHistory);
  }, [walletBalance, paymentHistory]);

  // Handle payment mode change (settle/credit)
  const handlePaymentModeChange = useCallback((mode: 'settle' | 'credit') => {
    setFormValues((prev) => ({ ...prev, paymentMode: mode }));
  }, []);

  // Handle payment method change - reset target form so it auto-populates with current remaining
  const handlePaymentMethodChange = useCallback((method: PaymentMethodType) => {
    setFormValues((prev) => ({
      ...prev,
      paymentMethod: method,
      // Reset target method's amount to 0 so it auto-populates with current remaining
      [method]: INITIAL_PAYMENT_VALUES[method],
    }));
    setErrors({});
  }, []);

  // Get current payment amount based on selected method
  const getCurrentPaymentAmount = useCallback((): number => {
    const { paymentMethod } = formValues;
    switch (paymentMethod) {
    case 'cash':
      return formValues.cash.amount;
    case 'online':
      return formValues.online.amount;
    case 'card':
      return formValues.card.amount;
    case 'cheque':
      return formValues.cheque.amount;
    case 'upi':
      return formValues.upi.amount;
    case 'wallet':
      return formValues.wallet.amount;
    default:
      return 0;
    }
  }, [formValues]);

  // Add payment to history
  const handleAddPayment = useCallback(() => {
    const amount = getCurrentPaymentAmount();
    const { paymentMethod } = formValues;

    // Validate amount
    const validation = validatePaymentAmount(amount, remainingAmount);
    if (!validation.valid) {
      setErrors({ amount: validation.message || 'Invalid amount' });
      return;
    }

    // Create payment history item
    const methodLabels: Record<PaymentMethodType, string> = {
      cash: 'Cash Payment',
      online: 'Bank',
      card: 'Card Payment',
      cheque: 'Cheque',
      upi: 'UPI',
      wallet: 'Wallet',
    };

    // Map payment method to payment mode name for ID lookup (case-insensitive)
    const methodToModeNameMap: Record<PaymentMethodType, string> = {
      cash: 'cash',
      online: 'online',
      card: 'card',
      cheque: 'cheque',
      upi: 'upi',
      wallet: 'wallet',
    };

    // Look up payment_mode_id from paymentModes based on name (case-insensitive)
    const modeName = methodToModeNameMap[paymentMethod];
    const matchedMode = paymentModes.find((mode) => mode.name?.toLowerCase() === modeName);
    const paymentModeId = matchedMode?.id ?? 0;

    const newPayment: PaymentHistoryItem = {
      id: generatePaymentId(),
      method: methodLabels[paymentMethod],
      amount,
      payment_mode_id: paymentModeId,
      date: getTodayYMD(),
    };

    // Add method-specific fields
    if (paymentMethod === 'cash') {
      newPayment.notes = formValues.cash.notes;
    } else if (paymentMethod === 'online') {
      newPayment.transactionNumber = formValues.online.transactionNumber;
      newPayment.paymentMode = formValues.online.paymentMode;
      newPayment.notes = formValues.online.notes;
    } else if (paymentMethod === 'card') {
      newPayment.transactionNumber = formValues.card.transactionNumber;
      newPayment.notes = formValues.card.notes;
    } else if (paymentMethod === 'upi') {
      newPayment.transactionNumber = formValues.upi.transactionNumber;
      const matchedProvider = upiProviders?.find(
        (p) => p.code.toLowerCase() === formValues.upi.provider?.toLowerCase()
      );
      if (matchedProvider) {
        newPayment.upiProvider = matchedProvider.name;
        newPayment.upiProviderId = matchedProvider.id;
      } else {
        newPayment.upiProvider = formValues.upi.provider;
      }
    } else if (paymentMethod === 'cheque') {
      newPayment.chequeNumber = formValues.cheque.chequeNumber;
      newPayment.chequeDate = formValues.cheque.date;
      newPayment.bankName = formValues.cheque.bankName;
      newPayment.notes = formValues.cheque.notes;
    } else if (paymentMethod === 'wallet') {
      newPayment.notes = formValues.wallet.notes;
      // Build credit_transaction_consumptions from selected advances
      // Group by package_id to create separate payment items per package
      const selectedAdvances = formValues.wallet.advances.filter((adv) => adv.isSelected && adv.consumeAmount > 0);
      if (selectedAdvances.length > 0) {
        // Group advances by package_id (undefined = non-package advances)
        const advancesByPackage = selectedAdvances.reduce((acc, adv) => {
          const key = adv.packageId ?? 'no-package';
          if (!acc[key]) acc[key] = [];
          acc[key].push(adv);
          return acc;
        }, {} as Record<string | number, typeof selectedAdvances>);

        // Create separate payment items for each package
        const packagePayments: PaymentHistoryItem[] = Object.entries(advancesByPackage).map(([key, advances]) => {
          const packageId = key === 'no-package' ? undefined : Number(key);
          const totalAmount = advances.reduce((sum, adv) => sum + adv.consumeAmount, 0);
          
          return {
            id: generatePaymentId(),
            method: newPayment.method,
            amount: totalAmount,
            payment_mode_id: newPayment.payment_mode_id,
            date: newPayment.date,
            notes: formValues.wallet.notes,
            package_id: packageId,
            credit_transaction_consumptions: advances.map((adv) => ({
              credit_transaction_id: adv.creditTransactionId!,
              consume_amount: adv.consumeAmount.toString(),
            })),
          };
        });

        // Add all package-grouped payments to history
        setPaymentHistory((prev) => [...prev, ...packagePayments]);

        // Reset current payment form
        setFormValues((prev) => ({
          ...prev,
          [paymentMethod]: INITIAL_PAYMENT_VALUES[paymentMethod],
        }));
        setErrors({});
        return; // Early return since we've already added to history
      }
    }

    setPaymentHistory((prev) => [...prev, newPayment]);

    // Reset current payment form
    setFormValues((prev) => ({
      ...prev,
      [paymentMethod]: INITIAL_PAYMENT_VALUES[paymentMethod],
    }));
    setErrors({});
  }, [formValues, getCurrentPaymentAmount, remainingAmount, paymentModes, generatePaymentId, upiProviders]);

  // Edit payment from history
  const handleEditPayment = useCallback((payment: PaymentHistoryItem) => {
    // Remove from history and populate form
    setPaymentHistory((prev) => prev.filter((p) => p.id !== payment.id));
    
    // Determine method and populate form (simplified)
    if (payment.method === 'Cash Payment') {
      setFormValues((prev) => ({
        ...prev,
        paymentMethod: 'cash',
        cash: { amount: payment.amount, notes: payment.notes || '' },
      }));
    }
    // Add similar logic for other payment methods as needed
  }, []);

  // Delete payment from history
  const handleDeletePayment = useCallback((paymentId: string | number) => {
    setPaymentHistory((prev) => prev.filter((p) => p.id !== paymentId));
  }, []);

  // Check if save should be enabled
  // When requireFullPayment is true, save is only enabled when the FULL amount is covered (no partial payments allowed)
  // When requireFullPayment is false (default), save is enabled when any valid payment is added
  const canSave = useMemo(() => {
    // Disable if parent mutation is pending (TanStack isPending)
    if (isSubmitting) {
      return false;
    }
    if (formValues.paymentMode === 'credit') {
      // Disable save if credit limit is exceeded
      const exceedsCreditLimit = maxCreditAmount !== undefined && paymentDetails.netAmount > maxCreditAmount;
      return !exceedsCreditLimit;
    }
    
    // If remaining amount is 0 or negative, full amount is already covered (by package/payments)
    if (remainingAmount <= 0) {
      return true;
    }
    
    const currentAmount = getCurrentPaymentAmount();
    
    // When requireFullPayment is true (e.g., check-in advance), require full coverage
    if (requireFullPayment) {
      // Check if current form payment covers the remaining amount
      if (currentAmount > 0 && currentAmount >= remainingAmount) {
        return true;
      }
      
      // For wallet method, check if selected advances cover the remaining amount
      if (formValues.paymentMethod === 'wallet' && formValues.wallet.advances.some((adv) => adv.isSelected && adv.consumeAmount > 0)) {
        const walletTotal = formValues.wallet.advances
          .filter((adv) => adv.isSelected && adv.consumeAmount > 0)
          .reduce((sum, adv) => sum + adv.consumeAmount, 0);
        if (walletTotal >= remainingAmount) {
          return true;
        }
      }
      
      return false;
    }
    
    // Default behavior: allow partial payments
    // Enable save when there's at least one payment explicitly added via "Add Payment"
    if (paymentHistory.length > 0) {
      return true;
    }
    // Enable save when no "Add Payment" done but form has valid current amount (direct save)
    if (currentAmount > 0 && currentAmount <= remainingAmount) {
      return true;
    }
    // Enable save when wallet method is selected and any advance has positive consume amount
    if (formValues.paymentMethod === 'wallet' && formValues.wallet.advances.some((adv) => adv.isSelected && adv.consumeAmount > 0)) {
      return true;
    }
    return false;
  }, [formValues.paymentMode, formValues.paymentMethod, formValues.wallet.advances, paymentHistory.length, maxCreditAmount, paymentDetails.netAmount, remainingAmount, getCurrentPaymentAmount, isSubmitting, requireFullPayment]);

  // Handle save with loading state
  // - If no "Add Payment" done (paymentHistory empty): auto-add current form value
  // - If "Add Payment" was done (paymentHistory has items): only use history, don't auto-add
  const handleSave = useCallback(async () => {
    // Prevent double-click: if already saving (local or parent mutation pending), exit early
    if (isLoading || isSubmitting) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      let finalPaymentHistory = [...paymentHistory];
      
      // Only auto-add current form value if no "Add Payment" was clicked
      if (paymentHistory.length === 0 && formValues.paymentMode === 'settle') {
        const currentAmount = getCurrentPaymentAmount();
        
        if (currentAmount > 0) {
          const { paymentMethod } = formValues;
          
          // Validate amount against remaining
          const validation = validatePaymentAmount(currentAmount, remainingAmount);
          if (!validation.valid) {
            setErrors({ amount: validation.message || 'Invalid amount' });
            setIsLoading(false);
            return;
          }
          
          // Create payment history item
          const methodLabels: Record<PaymentMethodType, string> = {
            cash: 'Cash Payment',
            online: 'Bank',
            card: 'Card Payment',
            cheque: 'Cheque',
            upi: 'UPI',
            wallet: 'Wallet',
          };

          // Map payment method to payment mode name for ID lookup (case-insensitive)
          const methodToModeNameMap: Record<PaymentMethodType, string> = {
            cash: 'cash',
            online: 'online',
            card: 'card',
            cheque: 'cheque',
            upi: 'upi',
            wallet: 'wallet',
          };

          // Look up payment_mode_id from paymentModes based on name (case-insensitive)
          const modeName = methodToModeNameMap[paymentMethod];
          const matchedMode = paymentModes.find((mode) => mode.name?.toLowerCase() === modeName);
          const paymentModeId = matchedMode?.id ?? 0;

          const newPayment: PaymentHistoryItem = {
            id: generatePaymentId(),
            method: methodLabels[paymentMethod],
            amount: currentAmount,
            payment_mode_id: paymentModeId,
            date: getTodayYMD(),
          };

          // Add method-specific fields
          if (paymentMethod === 'cash') {
            newPayment.notes = formValues.cash.notes;
          } else if (paymentMethod === 'online') {
            newPayment.transactionNumber = formValues.online.transactionNumber;
            newPayment.paymentMode = formValues.online.paymentMode;
            newPayment.notes = formValues.online.notes;
          } else if (paymentMethod === 'card') {
            newPayment.transactionNumber = formValues.card.transactionNumber;
            newPayment.notes = formValues.card.notes;
          } else if (paymentMethod === 'upi') {
            newPayment.transactionNumber = formValues.upi.transactionNumber;
            const matchedProv = upiProviders?.find(
              (p) => p.code?.toLowerCase() === formValues.upi.provider?.toLowerCase()
            );
            if (matchedProv) {
              newPayment.upiProvider = matchedProv.name;
              newPayment.upiProviderId = matchedProv.id;
            } else {
              newPayment.upiProvider = formValues.upi.provider;
            }
          } else if (paymentMethod === 'cheque') {
            newPayment.chequeNumber = formValues.cheque.chequeNumber;
            newPayment.chequeDate = formValues.cheque.date;
            newPayment.bankName = formValues.cheque.bankName;
            newPayment.notes = formValues.cheque.notes;
          } else if (paymentMethod === 'wallet') {
            newPayment.notes = formValues.wallet.notes;
            // Handle wallet advances
            const selectedAdvances = formValues.wallet.advances.filter((adv) => adv.isSelected && adv.consumeAmount > 0);
            if (selectedAdvances.length > 0) {
              const advancesByPackage = selectedAdvances.reduce((acc, adv) => {
                const key = adv.packageId ?? 'no-package';
                if (!acc[key]) acc[key] = [];
                acc[key].push(adv);
                return acc;
              }, {} as Record<string | number, typeof selectedAdvances>);

              const packagePayments: PaymentHistoryItem[] = Object.entries(advancesByPackage).map(([key, advances]) => {
                const packageId = key === 'no-package' ? undefined : Number(key);
                const totalAmount = advances.reduce((sum, adv) => sum + adv.consumeAmount, 0);
                
                return {
                  id: generatePaymentId(),
                  method: methodLabels[paymentMethod],
                  amount: totalAmount,
                  payment_mode_id: paymentModeId,
                  date: getTodayYMD(),
                  notes: formValues.wallet.notes,
                  package_id: packageId,
                  credit_transaction_consumptions: advances.map((adv) => ({
                    credit_transaction_id: adv.creditTransactionId!,
                    consume_amount: adv.consumeAmount.toString(),
                  })),
                };
              });

              finalPaymentHistory = [...finalPaymentHistory, ...packagePayments];
            } else {
              finalPaymentHistory = [...finalPaymentHistory, newPayment];
            }
          }
          
          // For non-wallet methods, add the payment
          if (paymentMethod !== 'wallet') {
            finalPaymentHistory = [...finalPaymentHistory, newPayment];
          }
        }
      }
      // If paymentHistory.length > 0, we use only what's in the history (no auto-add)
        
      await onSave(formValues, finalPaymentHistory);
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save payment:', error);
      setErrors({ save: 'Failed to save payment. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }, [formValues, paymentHistory, onSave, onClose, getCurrentPaymentAmount, remainingAmount, paymentModes, generatePaymentId, isLoading, isSubmitting, upiProviders]);

  // Handle close
  const handleClose = useCallback(() => {
    setFormValues(INITIAL_PAYMENT_VALUES);
    setPaymentHistory(existingPaymentHistory);
    setErrors({});
    onClose();
  }, [existingPaymentHistory, onClose]);

  // Render payment form based on selected method
  const renderPaymentForm = () => {
    if (formValues.paymentMode === 'credit') {
      const exceedsCreditLimit = maxCreditAmount !== undefined && paymentDetails.netAmount > maxCreditAmount;
      return (
        <CreditInfoBanner 
          exceedsCreditLimit={exceedsCreditLimit}
          maxCreditAmount={maxCreditAmount}
        />
      );
    }

    switch (formValues.paymentMethod) {
    case 'cash':
      return (
        <CashPaymentForm
          data={formValues.cash}
          onChange={(data) => setFormValues((prev) => ({ ...prev, cash: data }))}
          error={errors.amount}
          remainingAmount={remainingAmount}
        />
      );
    case 'online':
      return (
        <OnlinePaymentForm
          data={formValues.online}
          onChange={(data) => setFormValues((prev) => ({ ...prev, online: data }))}
          errors={{ amount: errors.amount }}
          remainingAmount={remainingAmount}
        />
      );
    case 'card':
      return (
        <CardPaymentForm
          data={formValues.card}
          onChange={(data) => setFormValues((prev) => ({ ...prev, card: data }))}
          errors={{ amount: errors.amount }}
          remainingAmount={remainingAmount}
        />
      );
    case 'cheque':
      return (
        <ChequePaymentForm
          data={formValues.cheque}
          onChange={(data) => setFormValues((prev) => ({ ...prev, cheque: data }))}
          errors={{ amount: errors.amount }}
          remainingAmount={remainingAmount}
        />
      );
    case 'upi':
      return (
        <UPIPaymentForm
          data={formValues.upi}
          onChange={(data) => setFormValues((prev) => ({ ...prev, upi: data }))}
          errors={{ amount: errors.amount }}
          remainingAmount={remainingAmount}
          upiProviders={upiProviders}
        />
      );
    case 'wallet': {
      // Compute already-used credit transaction IDs from payment history
      const usedCreditTransactionIds = paymentHistory
        .filter((p) => p.credit_transaction_consumptions && p.credit_transaction_consumptions.length > 0)
        .flatMap((p) => p.credit_transaction_consumptions!.map((c) => c.credit_transaction_id));
      
      // Handler to remove a wallet payment from history when user unchecks the consume checkbox
      const handleRemoveWalletFromHistory = (creditTransactionId: number) => {
        setPaymentHistory((prev) => 
          prev.filter((p) => {
            // Remove entries that contain this credit transaction
            if (p.credit_transaction_consumptions && p.credit_transaction_consumptions.length > 0) {
              const hasThisTransaction = p.credit_transaction_consumptions.some(
                (c) => c.credit_transaction_id === creditTransactionId
              );
              return !hasThisTransaction;
            }
            return true;
          })
        );
      };
      
      return (
        <WalletPaymentForm
          data={formValues.wallet}
          onChange={(data) => setFormValues((prev) => ({ ...prev, wallet: data }))}
          walletBalance={effectiveWalletBalance}
          advances={walletAdvances}
          currency={selectedCurrency}
          error={errors.amount}
          remainingAmount={remainingAmount}
          usedCreditTransactionIds={usedCreditTransactionIds}
          onRemoveFromHistory={handleRemoveWalletFromHistory}
        />
      );
    }
    default:
      return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title ?? tBilling('collectPayment.title', 'Collect Payment')}
        </Typography>
        <CloseIconButton onClick={handleClose} />
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ pt: 2 }}>
        {/* Payment Mode Toggle - Hidden when hidePaymentModeToggle is true */}
        {!hidePaymentModeToggle && (
          <PaymentModeToggle
            value={formValues.paymentMode}
            onChange={handlePaymentModeChange}
          />
        )}

        {/* Payment Details Card */}
        <PaymentDetailsCard 
          details={{ ...paymentDetails, currency: selectedCurrency }} 
          onCurrencyChange={setSelectedCurrency}
        />



        {/* Consume from Package Banner - only show when there's package consumption */}
        <ConsumePackageBanner
          consumeAmount={consumeFromPackage}
          currency={selectedCurrency}
          totalBill={paymentDetails.netAmount}
          pendingAmount={remainingAmount}
          packageBalance={packageBalance}
          packageName={packageName}
          bannerLabel={consumePackageBannerLabel}
          totalBillLabel={consumePackageTotalLabel}
          advanceDeductedLabel={consumePackageDeductedLabel}
          pendingAmountLabel={consumePackagePendingLabel}
        />

        {/* Payment History */}
        {paymentHistory.length > 0 && (
          <PaymentHistoryList
            payments={paymentHistory}
            currency={selectedCurrency}
            onEdit={handleEditPayment}
            onDelete={handleDeletePayment}
          />
        )}

        {/* Remaining Amount Display - hidden when fully covered by package */}
        {formValues.paymentMode === 'settle' && !isFullyCoveredByPackage && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: remainingAmount === 0 ? '#e8f5e9' : '#fff3e0',
              borderRadius: '8px',
              px: 2,
              py: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Remaining Amount
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600, 
                color: remainingAmount === 0 ? '#2e7d32' : '#ed6c02' 
              }}
            >
              {formatCurrency(remainingAmount, selectedCurrency)}
            </Typography>
          </Box>
        )}

        {/* Payment Method Selector - only show when settling and amount remaining */}
        {formValues.paymentMode === 'settle' && remainingAmount > 0 && (
          <PaymentMethodSelector
            value={formValues.paymentMethod}
            onChange={handlePaymentMethodChange}
            availableMethods={availablePaymentMethods}
          />
        )}

        {/* Payment Form - only show when amount remaining */}
        {remainingAmount > 0 && renderPaymentForm()}

        {/* Ready to Save Message - show when fully covered by package or payments complete */}
        {formValues.paymentMode === 'settle' && remainingAmount === 0 && (isFullyCoveredByPackage || paymentHistory.length > 0) && (
          <Box
            sx={{
              textAlign: 'center',
              py: 2,
              color: '#2e7d32',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              ✓ Payment complete. Click &apos;Save Payment&apos; to confirm.
            </Typography>
          </Box>
        )}

        {/* Save Error Display */}
        {errors.save && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errors.save}
          </Typography>
        )}

        {/* Add Payment Button - only show when settling and amount remaining */}
        {formValues.paymentMode === 'settle' && remainingAmount > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddPayment}
              disabled={getCurrentPaymentAmount() <= 0}
              startIcon={<AddOutlinedIcon />}
              sx={{ borderRadius: '8px' }}
            >
              Add Payment
            </Button>
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ borderRadius: '8px', px: 3 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary" 
          onClick={() => setSavePaymentConfirmOpen(true)}
          disabled={!canSave || isLoading}
          startIcon={!isLoading ? (formValues.paymentMode === 'credit' ? <CreditScoreOutlinedIcon /> : <SaveOutlinedIcon />) : undefined}
          sx={{
            borderRadius: '8px',
            px: 3,
            minWidth: 120,
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            formValues.paymentMode === 'credit' ? 'Save as Credit' : 'Save Payment'
          )}
        </Button>
      </DialogActions>

      {/* Save Payment Confirmation Dialog */}
      <ConfirmationModal
        open={savePaymentConfirmOpen}
        handleClose={() => setSavePaymentConfirmOpen(false)}
        onConfirm={() => {
          setSavePaymentConfirmOpen(false);
          // Set loading immediately to prevent double-click
          setIsLoading(true);
          handleSave();
        }}
        type="save"
        title={formValues.paymentMode === 'credit' ? 'Save as Credit' : 'Save Payment'}
        contentMessage={formValues.paymentMode === 'credit' 
          ? 'Are you sure you want to save this invoice as credit?' 
          : 'Are you sure you want to save this payment?'}
        isConfirming={isLoading || isSubmitting}
      />
    </Dialog>
  );
};

export default CollectPaymentModal;
